import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import api from '../../services/api';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

// Mirrors FormCrmMappingService's EXTRA_LEAD_FIELDS allowlist — these get
// auto-added to fieldMap when a Lead field is dragged in from the CRM Field
// Palette (Phase 15), shown here so they can be reviewed/unmapped without
// re-dragging.
const EXTRA_LEAD_FIELD_LABELS = {
  source: 'Lead Source', status: 'Lead Status', leadQuality: 'Lead Quality',
  lifecycleStage: 'Lifecycle Stage', expectedValue: 'Expected Value', currency: 'Currency',
  website: 'Website', location: 'Location', businessType: 'Business Type',
  utmSource: 'UTM Source', utmMedium: 'UTM Medium', utmCampaign: 'UTM Campaign',
  tags: 'Tags', lostReason: 'Lost Reason',
};

// Phase 16 — mirrors FormCrmMappingService's EXTRA_CONTACT_FIELDS/
// EXTRA_ACCOUNT_FIELDS allowlists. Unlike Lead (identity-matched via the
// fixed name/email/phone selects above), Contact/Account have no dedicated
// identity UI — dragging Contact Email or Account Name from the palette is
// how those get set, so these sections are purely review/unmap, same as
// the Lead one below.
const EXTRA_CONTACT_FIELD_LABELS = {
  name: 'Contact Name', phoneNumber: 'Phone', email: 'Email', company: 'Company',
  designation: 'Job Title', website: 'Website', street: 'Street Address', city: 'City',
  state: 'State', country: 'Country', zip: 'ZIP / Postal Code', birthday: 'Birthday',
  lifecycleStage: 'Lifecycle Stage', preferredChannel: 'Preferred Channel',
  doNotContact: 'Do Not Contact', tags: 'Tags', notes: 'Notes',
};
const EXTRA_ACCOUNT_FIELD_LABELS = {
  name: 'Account Name', industry: 'Industry', website: 'Website', email: 'Email',
  phone: 'Phone', street: 'Street Address', city: 'City', state: 'State',
  country: 'Country', zip: 'ZIP / Postal Code', gstin: 'GSTIN', currency: 'Currency',
  annualRevenue: 'Annual Revenue', employees: 'Employee Count', status: 'Account Status',
  tags: 'Tags', notes: 'Notes',
};

// Phase 17 — mirrors EXTRA_DEAL_FIELDS. `stage`/`pipelineId` are excluded:
// those come from the Pipeline/Stage picker below, fixed by the admin,
// never from a form field.
const EXTRA_DEAL_FIELD_LABELS = {
  title: 'Deal Title', value: 'Deal Value', currency: 'Currency', probability: 'Win Probability (%)',
  expectedCloseDate: 'Expected Close Date', competitorName: 'Competitor Name', tags: 'Tags', notes: 'Notes',
};

// Phase 23 — mirrors EXTRA_TASK_FIELDS.
const EXTRA_TASK_FIELD_LABELS = {
  title: 'Task Title', description: 'Description', type: 'Task Type', priority: 'Priority',
  dueDate: 'Due Date', notes: 'Notes',
};

function MappedFieldsSection({ title, labels, map, fields, onUnmap }) {
  const keys = Object.keys(map || {}).filter((key) => labels[key] && map[key]);
  if (!keys.length) return null;
  return (
    <div className="pt-4 border-t border-[#F0F1F3]">
      <label className={labelClass}>{title}</label>
      <div className="space-y-1.5">
        {keys.map((key) => {
          const matchedField = fields.find((f) => f.id === map[key]);
          return (
            <div key={key} className="flex items-center justify-between px-3 py-2 border border-[#D8DDE6] rounded-[4px]">
              <span className="text-[12px] text-[#181818]">
                <span className="font-bold">{labels[key]}</span>
                {' ← '}
                {matchedField?.label || 'Unknown field'}
              </span>
              <button onClick={() => onUnmap(key)} className="text-[#706E6B] hover:text-[#EA001E]" title="Unmap">
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Form-level bridge into the CRM (Phase 7) — distinct from ThemePanel
 * (design) and ConditionEditor (visibility). Maps a small, fixed set of
 * Lead identity fields (name/email/phone) to form-field ids; every other
 * submitted value still rides along as context on the created/matched
 * Lead's customFields, it just isn't a first-class mapped column. Mirrors
 * schema.settings.crmMapping, read by FormCrmMappingService server-side.
 */
/**
 * Phase 17 — lets the admin fix a Deal's Pipeline + starting Stage for this
 * form, since neither can safely come from an anonymous submission. Only
 * rendered once at least one Deal field has been dragged in (mirrors how
 * the Lead identity selects only show once fields exist).
 */
function DealPipelineStagePicker({ dealPipelineId, dealStage, onChange }) {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/crm/pipelines');
        setPipelines(res.data.pipelines || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedPipeline = pipelines.find((p) => p._id === dealPipelineId);
  const stages = [...(selectedPipeline?.stages || [])].sort((a, b) => a.order - b.order);

  if (loading) return <p className="text-[11px] text-[#706E6B]">Loading pipelines…</p>;
  if (!pipelines.length) {
    return <p className="text-[11px] text-[#EA001E]">No active pipeline found — create one in Deals settings first.</p>;
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>Pipeline</label>
        <select
          className={inputClass}
          value={dealPipelineId || ''}
          onChange={(e) => {
            const pipeline = pipelines.find((p) => p._id === e.target.value);
            const firstStage = [...(pipeline?.stages || [])].sort((a, b) => a.order - b.order)[0];
            onChange({ dealPipelineId: e.target.value || undefined, dealStage: firstStage?.stageId });
          }}
        >
          <option value="">Select a pipeline</option>
          {pipelines.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
      </div>
      {selectedPipeline && (
        <div>
          <label className={labelClass}>Starting Stage</label>
          <select
            className={inputClass}
            value={dealStage || ''}
            onChange={(e) => onChange({ dealStage: e.target.value || undefined })}
          >
            {stages.map((s) => <option key={s.stageId} value={s.stageId}>{s.name}</option>)}
          </select>
        </div>
      )}
      <p className="text-[11px] text-[#706E6B]">
        Every qualifying submission creates a new Deal in this stage — Deals aren't matched/updated
        by identity the way Leads/Contacts/Accounts are.
      </p>
    </div>
  );
}

export default function CrmMappingPanel({ mapping, fields, onChange, onClose }) {
  const current = mapping || { enabled: false, fieldMap: {} };
  const set = (patch) => onChange({ ...current, ...patch });
  const setField = (key, fieldId) => set({ fieldMap: { ...current.fieldMap, [key]: fieldId || undefined } });
  const unmapContact = (key) => set({ contactFieldMap: { ...current.contactFieldMap, [key]: undefined } });
  const unmapAccount = (key) => set({ accountFieldMap: { ...current.accountFieldMap, [key]: undefined } });
  const unmapTask = (key) => set({ taskFieldMap: { ...current.taskFieldMap, [key]: undefined } });
  const unmapDeal = (key) => set({ dealFieldMap: { ...current.dealFieldMap, [key]: undefined } });

  const hasIdentityField = !!(current.fieldMap?.email || current.fieldMap?.phone);
  const hasDealFields = Object.keys(current.dealFieldMap || {}).some((k) => current.dealFieldMap[k]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">CRM Mapping</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={!!current.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            className="accent-[#0176D3] w-4 h-4"
            disabled={!fields.length}
          />
          Create a Lead from each submission
        </label>

        {!fields.length && (
          <p className="text-[11px] text-[#706E6B]">Add at least one field to the form first.</p>
        )}

        {current.enabled && fields.length > 0 && (
          <div className="space-y-4">
            <p className="text-[11px] text-[#706E6B]">
              Map form fields to the Lead's identity — used to fill in Name/Email/Phone and to
              detect a returning visitor (matched by email or phone) instead of creating a duplicate.
            </p>

            {[['name', 'Name'], ['email', 'Email'], ['phone', 'Phone']].map(([key, label]) => (
              <div key={key}>
                <label className={labelClass}>{label}</label>
                <select
                  className={inputClass}
                  value={current.fieldMap?.[key] || ''}
                  onChange={(e) => setField(key, e.target.value)}
                >
                  <option value="">Not mapped</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
            ))}

            {!hasIdentityField && (
              <p className="text-[11px] text-[#EA001E]">
                Map Email or Phone — without one, submissions can't be matched to an existing Lead
                and no Lead will be created.
              </p>
            )}

            <p className="text-[11px] text-[#706E6B]">
              Every submitted answer is still attached to the Lead for context, even fields not
              mapped here — this only controls which answers fill in Name/Email/Phone.
            </p>

            <MappedFieldsSection
              title="Additional Lead Fields (from CRM Field Palette)"
              labels={EXTRA_LEAD_FIELD_LABELS}
              map={current.fieldMap}
              fields={fields}
              onUnmap={(key) => setField(key, '')}
            />
            <MappedFieldsSection
              title="Contact Fields (also creates/updates a Contact)"
              labels={EXTRA_CONTACT_FIELD_LABELS}
              map={current.contactFieldMap}
              fields={fields}
              onUnmap={unmapContact}
            />
            <MappedFieldsSection
              title="Account Fields (also creates/updates an Account)"
              labels={EXTRA_ACCOUNT_FIELD_LABELS}
              map={current.accountFieldMap}
              fields={fields}
              onUnmap={unmapAccount}
            />
            <MappedFieldsSection
              title="Deal Fields (also creates a Deal)"
              labels={EXTRA_DEAL_FIELD_LABELS}
              map={current.dealFieldMap}
              fields={fields}
              onUnmap={unmapDeal}
            />
            <MappedFieldsSection
              title="Task Fields (also creates a follow-up Task)"
              labels={EXTRA_TASK_FIELD_LABELS}
              map={current.taskFieldMap}
              fields={fields}
              onUnmap={unmapTask}
            />
            {hasDealFields && (
              <div className="pt-4 border-t border-[#F0F1F3]">
                <label className={labelClass}>Deal Pipeline & Stage</label>
                <DealPipelineStagePicker
                  dealPipelineId={current.dealPipelineId}
                  dealStage={current.dealStage}
                  onChange={(patch) => set(patch)}
                />
                {!current.dealPipelineId && (
                  <p className="text-[11px] text-[#EA001E] mt-2">
                    Select a pipeline — without one, no Deal will be created even though Deal
                    fields are mapped.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
